import { SafeHydrate } from "@/SafeHydrate";
import { useMemo } from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { isDuplicatePost } from "@/lib/posts";
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return "";
  }
}
function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").trim();
}
function ArticlesGridConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const filteredPosts = useMemo(() => {
    return (posts || []).filter(post => !isDuplicatePost(post));
  }, [posts]);
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {[0, 1, 2, 3].map(i => <div key={i} data-index={i} className="flex flex-col bg-card border border-border/80 rounded-xl overflow-hidden p-5 space-y-4 shadow-sm">
            <div className="aspect-[16/10] w-full bg-muted animate-pulse rounded-md" />
            <div className="flex gap-2">
              <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
              <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
            </div>
            <div className="h-6 w-5/6 bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-3/4 bg-muted animate-pulse rounded-md" />
          </div>)}
      </div>;
  }
  if (filteredPosts.length === 0) {
    return <div className="py-12 text-center">
        <p className="font-default text-muted-foreground">No strategic intelligence articles published yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {filteredPosts.map((post, i) => {
      const category = post.categories && post.categories[0]?.name || post.tags && post.tags[0]?.name || "Revenue Strategy";
      const date = formatDate(post.date);
      const excerpt = stripHtml(post.excerpt);
      return <article key={post.id} data-index={i} className="group flex flex-col justify-between bg-card border border-border/80 rounded-xl overflow-hidden p-5 shadow-sm transition-all duration-300 hover:shadow-md hover:border-secondary/40">
            <div>
              <Link href={post.link || "#"} className="block overflow-hidden rounded-md">
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                  {post.featuredImage ? <picture>
                      <source srcSet={`${post.featuredImage}.webp`} type="image/webp" />
                      <img src={post.featuredImage} alt={`${post.title} - Hotel revenue intelligence`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
                    </picture> : <div className="h-full w-full bg-muted/60 flex items-center justify-center text-muted-foreground text-xs font-mono">
                      HOTELREVBABA
                    </div>}
                </div>
              </Link>

              <div className="mt-6 flex items-center justify-between gap-3 text-xs">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-secondary/15 text-secondary-foreground font-mono text-[0.7rem] font-semibold tracking-wider uppercase">
                  {category}
                </span>
                {date && <span className="inline-flex items-center gap-1.5 font-mono text-muted-foreground text-[0.75rem]">
                    <SafeHydrate><Calendar className="size-3.5 text-secondary" /></SafeHydrate>
                    {date}
                  </span>}
              </div>

              <Link href={post.link || "#"} className="block mt-4 group/title">
                <h3 className="font-default text-lg font-bold text-foreground leading-snug tracking-tight transition-colors duration-200 group-hover/title:text-secondary-foreground">
                  {post.title}
                </h3>
              </Link>

              {excerpt && <p className="mt-3 font-default text-sm leading-relaxed text-muted-foreground line-clamp-2">
                  {excerpt}
                </p>}
            </div>

            <div className="mt-6 pt-4 border-t border-border/60">
              <Link href={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-wider text-primary hover:text-secondary-foreground transition-colors duration-200 uppercase">
                Read Analysis
                <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          </article>;
    })}
    </div>;
}
export default function LatestRevenueInsights() {
  return <section data-section-id="4693" id="latest-revenue-insights" className="bg-background text-foreground py-20 md:py-28 px-6 md:px-10 border-t border-border/40">
      <div className="mx-auto max-w-7xl">
        {/* Header Row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold tracking-[0.25em] text-secondary-foreground uppercase">
              Intelligence &amp; Field Notes
            </p>
            <h2 className="mt-3 font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
              Latest Strategic Insights &amp; Case Studies
            </h2>
          </div>

          <div>
            <Link to="/InsightsPage" className="group inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.15em] uppercase text-foreground hover:text-secondary-foreground transition-colors duration-200">
              <span>View All Articles</span>
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* 4-Column Editorial Grid */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 4,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}